import actions from './actions'
import api from 'lib/api/secondStepApi'
import reportsApi from 'lib/api/reportsApi'
import v4Api from 'lib/api/ssapi/secondStepApi.v4'
import { operations as ssapiOperations } from 'store/ssapiManager'
import { actions as ssapiActions } from 'store/ssapiManager/reducer'
import { isV4Routed } from 'lib/api/ssapi/secondStepApi.router'
import { formatError } from 'utils/formatError'
import licenseTypes from '../licenseManager/types'
import {
  getUserContextProgramPreferences,
  setSitePreferencesCalculatedFields,
} from 'utils/siteData'
import { types as userContextTypes } from 'auth/stores/userContext'
import { pacingNodesData } from 'components/PacingModal/data'
import { enrichSiteUsersWithClassCounts } from './helpers'

export const SHOULD_SHOW_IN_MODAL = true

/**
 * Maps nodeFriendlyName from v4 API to nodeKey for highSchool pacing dates
 * @param {string} nodeFriendlyName - The friendly name from the API (e.g., "hs b&c collection 1")
 * @returns {string|null} - The corresponding nodeKey or null if not found
 */
const mapFriendlyNameToNodeKey = nodeFriendlyName => {
  if (!nodeFriendlyName) return null

  const mapping = {
    'hs program training': pacingNodesData.highSchool.nodeTraining,
    'hs b&c collection 1': pacingNodesData.highSchool.node1,
    'hs b&c collection 2': pacingNodesData.highSchool.node2,
    'hs b&c collection 3': pacingNodesData.highSchool.node3,
    'hs c&c collection 1': pacingNodesData.highSchool.node4,
    'hs c&c collection 2': pacingNodesData.highSchool.node5,
    'hs c&c collection 3': pacingNodesData.highSchool.node6,
    'hs a&o collection 1': pacingNodesData.highSchool.node7,
    'hs a&o collection 2': pacingNodesData.highSchool.node8,
    'hs a&o collection 3': pacingNodesData.highSchool.node9,
    'hs w&c collection 1': pacingNodesData.highSchool.node10,
    'hs w&c collection 2': pacingNodesData.highSchool.node11,
    'hs w&c collection 3': pacingNodesData.highSchool.node12,
  }

  return mapping[nodeFriendlyName] || null
}

/**
 * Transforms v4 API pacing dates response to match expected format
 * Converts nodeFriendlyName to nodeKey and ensures proper structure
 * @param {Array} pacingDates - Array of pacing date objects from v4 API
 * @param {string} programName - The program name (e.g., 'highSchool')
 * @returns {Array} - Transformed pacing dates array
 */
const transformV4PacingDates = (pacingDates, programName) => {
  if (!Array.isArray(pacingDates)) return pacingDates

  // Only transform highSchool pacing dates
  if (programName !== 'highSchool' && programName !== 'highschool') {
    return pacingDates
  }

  return pacingDates.map(pacingDate => {
    // Ensure we have a valid pacing date object
    if (!pacingDate || typeof pacingDate !== 'object') {
      return pacingDate
    }

    // If already has nodeKey, ensure it's valid and return (already transformed or v3 format)
    if (pacingDate.nodeKey) {
      // Ensure nodeLevel is set for training node if missing
      if (
        pacingDate.nodeFriendlyName === 'hs program training' &&
        !pacingDate.nodeLevel
      ) {
        return {
          ...pacingDate,
          nodeLevel: 'training',
        }
      }
      return pacingDate
    }

    // Transform v4 format (with nodeFriendlyName) to expected format
    const nodeKey = mapFriendlyNameToNodeKey(pacingDate.nodeFriendlyName)
    const transformed = {
      ...pacingDate,
      nodeKey,
    }

    // Set nodeLevel for training node
    if (pacingDate.nodeFriendlyName === 'hs program training') {
      transformed.nodeLevel = 'training'
    }

    return transformed
  })
}

const updateSitePreferences = (siteId, sitePreferences) => {
  return async (dispatch, getState) => {
    dispatch(actions.savingSitePreferences(siteId))
    try {
      let result
      if (isV4Routed('updateSitePreferences')) {
        const data = await dispatch(
          ssapiOperations.updateSitePreferences(siteId, sitePreferences),
        )
        result = { data }
      } else {
        result = await api.updateSitePreferences(siteId, sitePreferences)
      }
      const siteData = result.data
      dispatch(actions.savingSitePreferencesSuccess(siteId))

      // we only expect 1 site preference set being changed at once
      const sitePreferenceProgram = Object.keys(sitePreferences)[0]
      const userProgramPreferencesAndCourses = getUserContextProgramPreferences(
        siteId,
        sitePreferenceProgram,
        getState()?.userContextManager?.sites,
      )

      const contextHighSchoolAreUsersAdded =
        userProgramPreferencesAndCourses?.preferences?.areUsersAdded || false
      if (sitePreferences?.highSchool) {
        sitePreferences.highSchool.areUsersAdded =
          contextHighSchoolAreUsersAdded || false
      }
      const sites = getState()?.licenseManager?.sites
      const site = sites?.find(sites => sites.id === siteId) || {}
      const sitePrefs = site?.sitePreferences || {}
      const sitePrefProg = sitePrefs[sitePreferenceProgram] || {}
      // TODO: need to revisit this calculation when we align all preferences into either context or site preferences
      const preferencesWithCalculatedFields = setSitePreferencesCalculatedFields(
        {
          ...sitePrefs,
          [sitePreferenceProgram]: {
            ...sitePrefProg,
            ...sitePreferences[sitePreferenceProgram],
          },
        },
        site,
      )
      const mspPreferencesWithCalculatedFields = Object.keys(
        sitePreferences,
      ).find(key => key === 'mspDigital')
        ? setSitePreferencesCalculatedFields({
            ...sitePrefs,
            mspDigital: {
              ...sitePrefs.mspDigital,
              ...sitePreferences.mspDigital,
            },
          })
        : preferencesWithCalculatedFields
      dispatch({
        type: licenseTypes.UPDATE_SITE,
        payload: {
          siteId,
          sitePreferences: {
            highSchool: preferencesWithCalculatedFields.highSchool,
            k5Digital: preferencesWithCalculatedFields.k5Digital,
            mspDigital: mspPreferencesWithCalculatedFields.mspDigital,
          },
        },
      })

      const calculatedProgramPreferences =
        preferencesWithCalculatedFields[sitePreferenceProgram]
      const userSiteContext =
        getState()?.userContextManager?.sites?.find(
          site => site.siteId === siteId,
        ) || {}

      const programContextPreferences =
        userSiteContext?.programs?.[sitePreferenceProgram]?.preferences || {}
      dispatch({
        type: userContextTypes.UPDATE_SITE_PROGRAM_PREFERENCES_TYPE,
        payload: {
          siteId,
          programName: sitePreferenceProgram,
          preferences: {
            isImplementationPlanComplete:
              calculatedProgramPreferences.isImplementationPlanComplete,
            isSetup:
              programContextPreferences.isSetup ||
              (programContextPreferences.isPacingComplete &&
                programContextPreferences?.areUsersAdded) ||
              false,
          },
        },
      })

      const { pacing, schoolYearEndDate } = siteData || {}
      let pacingDatesForProgram = pacing?.[sitePreferenceProgram] || null
      if (pacingDatesForProgram === null) {
        // temp check to try lowercase program name
        pacingDatesForProgram =
          pacing?.[sitePreferenceProgram.toLowerCase()] || null
      }
      // Transform v4 API response if needed
      if (isV4Routed('updateSitePreferences') && pacingDatesForProgram) {
        pacingDatesForProgram = transformV4PacingDates(
          pacingDatesForProgram,
          sitePreferenceProgram,
        )
      }
      dispatch({
        type: licenseTypes.UPDATE_SITE_PACING_DATES,
        payload: {
          siteId,
          pacingDates: pacingDatesForProgram,
          programName: sitePreferenceProgram,
          schoolYearEndDateString: schoolYearEndDate,
        },
      })
    } catch (error) {
      await dispatch(
        actions.savingSitePreferencesError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const updateSitePacingDates = (siteId, programName, pacingUpdate) => {
  return async (dispatch, getState) => {
    dispatch(actions.savingSitePacingDates())
    try {
      let pacingDates
      if (isV4Routed('updateSitePacingDates')) {
        const data = await dispatch(
          ssapiOperations.updateSitePacingDates(
            siteId,
            programName,
            pacingUpdate,
          ),
        )
        pacingDates = transformV4PacingDates(data, programName)
      } else {
        const response = await api.updateSitePacingDates(
          siteId,
          programName,
          pacingUpdate,
        )
        pacingDates = response.data
      }
      dispatch(actions.savingSitePacingDatesSuccess(pacingDates, programName))
      dispatch({
        type: licenseTypes.UPDATE_SITE_PACING_DATES,
        payload: {
          siteId,
          pacingDates,
          programName,
          schoolYearEndDateString: pacingUpdate.schoolYearEndDate.toISOString(),
        },
      })

      // Also update v4 sites store if v4 is being used
      if (isV4Routed('updateSitePacingDates')) {
        dispatch(
          ssapiActions.updateSitePacingDates({
            siteId,
            pacingDates,
            programName,
            schoolYearEndDateString: pacingUpdate.schoolYearEndDate.toISOString(),
          }),
        )
      }

      // currently this code is assuming a highschool context
      const userSiteContext =
        getState()?.userContextManager?.sites?.find(
          site => site.siteId === siteId,
        ) || {}

      const programContextPreferences =
        userSiteContext?.programs?.[programName]?.preferences || {}
      const newCalculatedPreferences = {
        isPacingComplete: true,
        isSetup:
          programContextPreferences.isSetup ||
          (programContextPreferences.isImplementationPlanComplete &&
            programContextPreferences?.areUsersAdded),
      }
      dispatch({
        type: userContextTypes.UPDATE_SITE_PROGRAM_PREFERENCES_TYPE,
        payload: {
          siteId,
          programName,
          preferences: newCalculatedPreferences,
        },
      })

      const sites = getState()?.licenseManager?.sites
      const site = sites?.find(sites => sites.id === siteId) || {}
      const sitePrefs = site?.sitePreferences || {}
      // TODO: need to revisit this calculation when we align all preferences into either context or site preferences
      const preferencesWithCalculatedFields = setSitePreferencesCalculatedFields(
        {
          ...sitePrefs,
          [programName]: {
            ...sitePrefs[programName],
            ...newCalculatedPreferences,
          },
        },
        site,
      )
      dispatch({
        type: licenseTypes.UPDATE_SITE,
        payload: { siteId, sitePreferences: preferencesWithCalculatedFields },
      })
    } catch (error) {
      dispatch(
        actions.savingSitePacingDatesError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const updateMSPOptIn = mspOptInArr => {
  return async dispatch => {
    dispatch(actions.updateMspOptInPrefences())
    try {
      const data = await dispatch(
        ssapiOperations.updateMspOptInSettings(mspOptInArr),
      )
      dispatch(actions.updateMspOptInPrefencesSuccess(data))

      // Update ssapiManager with the new response data
      dispatch(ssapiActions.updateSitesMspOptIn(data.createdItems || []))

      // Update userContext with preferences from the response
      data.createdItems?.forEach(createdItem => {
        if (!createdItem?.siteId || !Array.isArray(createdItem.preferences)) {
          return
        }

        // Find the BullyingHarassmentUnitEnabled preference and extract its value
        const bullyingHarassmentPref = createdItem.preferences.find(
          pref =>
            [
              'BullyingHarassmentUnitEnabled',
              'bullying_harassment_unit_enabled',
            ].includes(pref?.name) && pref?.accessCode === 'mspDigital',
        )

        if (bullyingHarassmentPref) {
          dispatch({
            type: userContextTypes.UPDATE_SITE_PROGRAM_PREFERENCES_TYPE,
            payload: {
              siteId: createdItem.siteId,
              programName: 'mspDigital',
              preferences: {
                bullyingHarassmentUnitEnabled: bullyingHarassmentPref.value,
              },
            },
          })
        }
      })
    } catch (error) {
      await dispatch(
        actions.updateMspOptInPrefencesError({
          error: formatError(error, SHOULD_SHOW_IN_MODAL),
        }),
      )
      throw error
    }
  }
}

const updateSiteUserPreferences = (siteId, sitePreferences) => {
  return async dispatch => {
    dispatch(actions.updatingUserProgramPreferences())
    try {
      let data
      if (isV4Routed('updateSiteUserPreferences')) {
        data = await dispatch(
          ssapiOperations.updateSiteUserPreferences(siteId, sitePreferences),
        )
      } else {
        const response = await api.updateSiteUserPreferences(
          siteId,
          sitePreferences,
        )
        data = response.data
        const sitePreferencesUpdated = data?.sitePreferences || {}
        // only write to the v3 data store when using v3 api:
        dispatch({
          type: licenseTypes.UPDATE_SITE,
          payload: { siteId, sitePreferences: sitePreferencesUpdated },
        })
      }
      dispatch(actions.updatingUserProgramPreferencesSuccess(data))
    } catch (error) {
      await dispatch(actions.updatingUserProgramPreferencesError(error))
      throw error
    }
  }
}

const fetchSiteUsers = siteId => {
  return async (dispatch, getState) => {
    if (!siteId) return

    const siteUsersData = getState()?.siteManager?.siteUsers?.data
    if (siteUsersData?.[siteId]) return

    dispatch(actions.fetchingSiteUsers())
    try {
      const [{ data: siteUsers }, k8SiteUsersResponse] = await Promise.all([
        v4Api.getSiteUsers(siteId),
        reportsApi.getSiteUsers(siteId).catch(() => null),
      ])
      const k8SiteUsers =
        k8SiteUsersResponse?.data?.data?.SiteUsers?.users || []
      const enrichedSiteUsers = enrichSiteUsersWithClassCounts(
        siteUsers,
        k8SiteUsers,
      )

      dispatch(
        actions.fetchingSiteUsersSuccess({
          siteId,
          siteUsers: enrichedSiteUsers,
        }),
      )
    } catch (error) {
      dispatch(
        actions.fetchingSiteUsersError(
          formatError(error, SHOULD_SHOW_IN_MODAL),
        ),
      )
    }
  }
}

export default {
  updateMSPOptIn,
  updateSitePreferences,
  updateSitePacingDates,
  updateSiteUserPreferences,
  fetchSiteUsers,
}
